import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, Observable } from 'rxjs';
import { FuseVerticalNavigationPosition } from '@fuse/components/navigation';
import { StorageUtilsService } from 'app/services/storage-utils/storage-utils.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root'
})
export class LanguagesService {
    private _translocoService = inject(TranslocoService);
    private _storageUtilsService = inject(StorageUtilsService);
    private _currentLanguage = new BehaviorSubject<string | null>(null);
    currentLanguage$: Observable<string | null> = this._currentLanguage.asObservable();

    /** temporarry config for ngx-translate */
    private _translateService: TranslateService = inject(TranslateService);


    constructor() {
        this.initializeLanguage();
    }

    private initializeLanguage(): void {
        /**  check if language exist in our localStorage */
        const savedLanguage = this._storageUtilsService.getLanguageFromStorage();

        /** one-time ngx-translate setup */
        this._translateService.addLangs(['fr', 'dz']);

        /** set initial language */
        if (savedLanguage) {
            this._translateService.setDefaultLang(savedLanguage);
            this.setLanguage(savedLanguage);
        } else {
            // Fallback to default language if no saved language
            const defaultLang = this._translocoService.getDefaultLang();
            this._translateService.setDefaultLang(defaultLang);
            this.setLanguage(defaultLang);
        }

        // Listen to language changes
        this._translocoService.langChanges$.subscribe(
            (lang: string) => this.updateLanguage(lang)
        );
    }

    // New method to update language
    private updateLanguage(lang: string): void {
        /** update localStorage */
        this._storageUtilsService.setLanguage(lang);

        /** update current language subject */
        this._currentLanguage.next(lang);
    }

    setLanguage(lang: string): void {
        /** set lang on local sotrage */
        this._storageUtilsService.setLanguage(lang);

        /** set lang using transloco */
        this._translocoService.setActiveLang(lang);

        /** update current language subject */
        this._currentLanguage.next(lang);

        /** set temporary ngx-translate config */
        this.setNgxTranslateConfig(lang);
    }

    /** get current lang value */
    getCurrentLanguage(): string | null {
        return this._currentLanguage.value;
    }

    /** get language direction */
    getLanguageDirection(lang: string): 'ltr' | 'rtl' {
        return 'dz' === lang ? 'rtl' : 'ltr';
    }

    /** get direction if left or right */
    getSidebarLeftOrRightPositon(lang: string): FuseVerticalNavigationPosition {
        return lang === 'dz' ? 'right' : 'left';
    }

    /** ngx-translate config */
    setNgxTranslateConfig(langKey): void {
        // Add languages
        this._translateService.addLangs(['fr', 'dz']);

        // Set the default language
        this._translateService.setDefaultLang(langKey);

        // Use a language
        this._translateService.use(langKey);
    }


}
